import { ChangeEvent, ReactElement, useEffect, useRef, useState } from 'react';
import Image from 'next/legacy/image';
import { JoinedFormFieldProps } from 'types';
import { COMPARING_FIELDS_MAP, EMPTY_FIELD_NOTIFICATION } from 'consts';
import {
  getComparingFieldValue,
  getConfirmPasswordErrorMessage,
  getNumberRangeErrorMessage,
  getSimpleLengthErrorMessage,
} from 'utils/validators';

import cx from 'classnames';
import styles from './Inputs.module.scss';

export type DynamicInputProps = JoinedFormFieldProps & {
  specialName?: string;
  withIndent?: boolean;
  hidePlaceholder?: boolean;
  forceValue?: boolean;
  preview?: (currentValue: string) => ReactElement | string;
};

export const DynamicInput = ({
  autocomplete,
  child,
  customClass,
  errorMessage,
  fieldName = '',
  specialName = '',
  from = 0,
  hidePlaceholder,
  id,
  isDisabled,
  isThin = false,
  withIndent = true,
  label,
  starRequiredLabel,
  maxLength,
  onChange,
  placeholder = '',
  prefix,
  required,
  showErrors,
  suffix,
  to = 999999,
  trackFieldChanges,
  trackFieldErrors,
  type = 'text',
  validator,
  value,
  forceValue,
  preview,
}: DynamicInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [currentValue, setCurrentValue] = useState('');
  const [errorText, setErrorText] = useState<string>(errorMessage || EMPTY_FIELD_NOTIFICATION || '');
  const [isValid, setIsValid] = useState(!required || (required && value));
  const [currentType, setCurrentType] = useState(type);
  const isComparable = !!COMPARING_FIELDS_MAP[fieldName];
  const isNotEmpty = !!currentValue || typeof currentValue === 'number';

  useEffect(() => {
    if(value!==currentValue) {
      onChange && onChange(fieldName, value);
      trackFieldChanges && trackFieldChanges(fieldName, currentValue !== value);
      setCurrentValue(value || '');
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    trackFieldErrors && trackFieldErrors(isValid, fieldName, errorText);
  }, [required]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    trackFieldChanges && trackFieldChanges(fieldName, currentValue !== value);
    if (!required && !currentValue) {
      setError('');
      return;
    }
    if (isComparable) {
      validateComparableField(currentValue);
    } else if (type === 'number') {
      validateNumberField(currentValue);
    } else {
      validateCommonTextField(currentValue);
    }
  }, [currentValue, required]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    trackFieldErrors && trackFieldErrors(!required || (required && value), fieldName, errorText);
    return () => {
      trackFieldErrors && trackFieldErrors(true, fieldName, '');
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const validateComparableField = (value: string) => {
    const isMatchPassword = !value ? false : value === getComparingFieldValue(fieldName);
    if (isMatchPassword) {
      setError('');
    } else {
      setError(getConfirmPasswordErrorMessage(currentValue, isMatchPassword) as string);
    }
  };

  const validateCommonTextField = (value: string) => {
    if (!value && required) {
      setError(errorMessage || 'This field is required');
    } else if (!isDisabled && validator && typeof validator !== 'string') {
      setError(validator(value, placeholder, to, from) as string);
    } else if (!isDisabled && !validator && (from || to)) {
      setError(getSimpleLengthErrorMessage(value, from, to));
    } else {
      setError('');
    }
  };

  const validateNumberField = (value: string) => {
    if (!value && required) {
      setError(errorMessage || 'This field is required');
    } else if (!isDisabled && validator && typeof validator !== 'string') {
      setError(validator(value, from, to, errorMessage, placeholder) as string);
    } else {
      setError(getNumberRangeErrorMessage(value, from as number, to as number, errorMessage, placeholder));
    }
  };

  const setError = (error: string) => {
    setIsValid(!error);
    setErrorText(error as string);
    trackFieldErrors && trackFieldErrors(!error, fieldName, error);
  };

  const trackChanges = (event: ChangeEvent<HTMLInputElement>) => {
    const value = suffix ? event.target.value.replace(suffix || '', '') : event.target.value;
    if (suffix && inputRef?.current) {
      inputRef.current.setSelectionRange(value.length, value.length);
      inputRef.current.blur();
      inputRef.current.focus();
    }
    onChange && onChange(fieldName, value);
    setCurrentValue(value);
  };

  const handleInputCapture = (event: ChangeEvent<HTMLInputElement>) => {
    trackChanges(event);
  };

  const handleTogglePasswordView = () => {
    setCurrentType((current) => {
      if (current === 'password') {
        return 'text';
      }
      return 'password';
    });
  };

  const handleOnFocus = () => {
    setIsFocused(true);
    if (suffix && inputRef?.current) {
      const lastValueSymbolIndex = inputRef?.current?.value.length - suffix.length;
      inputRef?.current?.setSelectionRange(lastValueSymbolIndex, lastValueSymbolIndex);
    }
  };

  const handleOnBlur = () => {
    setIsFocused(false);
  };

  const additionalProps: Record<string, string> = {};

  if (currentType === 'number') {
    additionalProps.inputMode = 'decimal';
  }
  if (forceValue && value) {
    additionalProps.value = value;
  }

  const warnAboutError = !isValid && showErrors;
  const showErrorMessages = errorText && warnAboutError;

  return (
    <div className={styles.outWrapper}>
      {label && <p className={styles.label}>{label}{starRequiredLabel && required ? (<span style={{ color: 'red' }}> *</span>) : null}</p>}
      <div className={styles.wrapper}>
        <div
          className={cx(styles.container, {
            [styles.indent]: !showErrorMessages && withIndent,
            [styles.thin]: isThin,
            [styles.containerError]: showErrorMessages,
          })}
        >
          <input
            id={id}
            ref={inputRef}
            type={currentType === 'number' ? 'text' : currentType}
            value={suffix && currentValue ? `${currentValue}${suffix}` : currentValue}
            className={cx(styles.input, {
              [styles.invalid]: warnAboutError,
              [styles.withErrorMessage]: showErrorMessages,
              [styles.disabled]: isDisabled,
              [styles.thin]: isThin,
              [styles.thinWithHidePlaceholder]: isThin && hidePlaceholder,
              [styles.withPrefix]: !!prefix,
            })}
            name={specialName || fieldName}
            required={required}
            onChange={trackChanges}
            onInput={handleOnFocus}
            onInputCapture={handleInputCapture}
            onFocus={handleOnFocus}
            onClick={handleOnFocus}
            onBlur={handleOnBlur}
            aria-invalid={!isValid}
            aria-required={required}
            placeholder={''}
            maxLength={maxLength}
            disabled={isDisabled}
            {...additionalProps}
            autoComplete={autocomplete}
          />

          {prefix && (
            <p
              className={cx(styles.prefix, {
                [styles.prefixMowed]: (isFocused || isNotEmpty) && !hidePlaceholder,
                [styles.thinInputPrefixMoved]: ((isFocused && isThin) || (isNotEmpty && isThin)) && !hidePlaceholder,
              })}
            >
              {prefix}
            </p>
          )}

          <p
            className={cx(styles.placeholder, {
              [styles.placeholderMowedThin]: isThin && (isFocused || isNotEmpty),
              [styles.placeholderMowed]: isFocused || isNotEmpty,
              [styles.placeholderWithPrefix]: !!prefix,
              [styles.hidePlaceholder]: hidePlaceholder && (isFocused || isNotEmpty),
            })}
          >
            {placeholder} {suffix && suffix}
          </p>

          {warnAboutError && (
            <div className={styles.warnIcon}>
              <Image src="/icons/security/WarningCircleWarn.svg" width={24} height={24} alt="Error" />
            </div>
          )}

          {type === 'password' && (
            <div className={cx(styles.icon, { [styles.passwordError]: warnAboutError })}>
              <Image
                src={currentType === 'password' ? '/icons/people/Eye.svg' : '/icons/people/EyeSlash.svg'}
                width={24}
                height={24}
                alt="Showing password"
                onClick={handleTogglePasswordView}
              />
            </div>
          )}
        </div>

        {child && <div className={cx(styles.child, customClass)}>{child}</div>}
      </div>

      {preview && (
        <p className={styles.previewContainer}>{preview(currentValue)}</p>
      )}

      {showErrorMessages && <div className={styles.errorMessage}>{<p>{errorText}</p>}</div>}
    </div>
  );
};
